import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { TextField } from "@excalidraw/excalidraw/components/TextField";
import {
  PlusIcon,
  TrashIcon,
  ZoomResetIcon,
} from "@excalidraw/excalidraw/components/icons";
import { useCallback, useEffect, useState } from "react";

import { BoardStorage } from "../data/BoardStorage";
import { isBoardsApiEnabled } from "../data/boardsApi";

import "./BoardsDialog.scss";

import type { BoardLoadProgress } from "../data/BoardStorage";
import type { BoardMeta } from "../data/boardsApi";

type BoardsDialogProps = {
  currentBoardId: string | null;
  loadingBoardId: string | null;
  loadProgress: BoardLoadProgress | null;
  onClose: () => void;
  onSwitchBoard: (
    boardId: string,
    opts?: { skipSave?: boolean },
  ) => Promise<void> | void;
  onBoardCreated: (boardId: string) => Promise<void> | void;
};

const formatTime = (ts: number) => {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return "";
  }
};

export const BoardsDialog = ({
  currentBoardId,
  loadingBoardId,
  loadProgress,
  onClose,
  onSwitchBoard,
  onBoardCreated,
}: BoardsDialogProps) => {
  const [boards, setBoards] = useState<BoardMeta[]>([]);
  const [busy, setBusy] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState("");

  const isSwitching = Boolean(loadingBoardId);
  const controlsDisabled = busy || isSwitching || listLoading || refreshing;

  const loadFromCache = useCallback(async () => {
    setListLoading(true);
    setError("");
    try {
      const list = await BoardStorage.listBoardsLocalFirst();
      setBoards(list);
    } catch (err: any) {
      setError(err?.message || "加载画板列表失败");
    } finally {
      setListLoading(false);
    }
  }, []);

  const forceRefreshFromCloud = useCallback(async () => {
    setRefreshing(true);
    setError("");
    try {
      const list = await BoardStorage.refreshBoardsFromRemote();
      setBoards(list);
    } catch (err: any) {
      setError(err?.message || "刷新画板列表失败");
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadFromCache();
  }, [loadFromCache]);

  const createBoard = async () => {
    setBusy(true);
    setError("");
    try {
      const list = await BoardStorage.createBoard("未命名画板");
      setBoards(list);
      const created = list[0];
      if (created) {
        await onBoardCreated(created.id);
      }
    } catch (err: any) {
      setError(err?.message || "创建画板失败");
    } finally {
      setBusy(false);
    }
  };

  const renameBoard = async (boardId: string) => {
    setBusy(true);
    setError("");
    try {
      const list = await BoardStorage.renameBoard(boardId, draftTitle);
      setBoards(list);
      setEditingId(null);
    } catch (err: any) {
      setError(err?.message || "重命名失败");
    } finally {
      setBusy(false);
    }
  };

  const deleteBoard = async (boardId: string) => {
    if (!window.confirm("确定删除这个画板？此操作不可撤销。")) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      const wasCurrent = currentBoardId === boardId;
      const list = await BoardStorage.deleteBoard(boardId);
      setBoards(list);
      if (wasCurrent) {
        const nextId = BoardStorage.getCurrentBoardId();
        if (nextId) {
          // Do not sync the deleted board's canvas onto the next board
          await onSwitchBoard(nextId, { skipSave: true });
        }
      }
    } catch (err: any) {
      setError(err?.message || "删除失败");
    } finally {
      setBusy(false);
    }
  };

  const showListProgress = listLoading || refreshing;

  return (
    <Dialog
      onCloseRequest={isSwitching ? () => {} : onClose}
      title={
        <span className="boards-dialog__title">
          <span>画板</span>
          <button
            type="button"
            className={`boards-dialog__refresh${
              refreshing ? " boards-dialog__refresh--spinning" : ""
            }`}
            aria-label="从云端刷新画板列表"
            title="从云端刷新"
            disabled={controlsDisabled && !refreshing}
            onClick={() => {
              void forceRefreshFromCloud();
            }}
          >
            {ZoomResetIcon}
          </button>
        </span>
      }
      size="regular"
    >
      <div className="boards-dialog">
        <div className="boards-dialog__toolbar">
          <div className="boards-dialog__hint">
            {isBoardsApiEnabled()
              ? "已启用云同步（D1 元数据 + R2 场景/图片）"
              : "未启用云同步：缺少 boards-config / API 配置"}
          </div>
          <FilledButton
            label="新建画板"
            icon={PlusIcon}
            onClick={() => {
              void createBoard();
            }}
            disabled={controlsDisabled}
          >
            新建画板
          </FilledButton>
        </div>

        {error && <div className="boards-dialog__error">{error}</div>}

        {showListProgress && (
          <div className="boards-dialog__progress" role="status">
            <div className="boards-dialog__progress-row">
              <span className="boards-dialog__progress-spinner" aria-hidden />
              <span>
                {refreshing ? "正在从云端刷新列表…" : "正在加载画板列表…"}
              </span>
            </div>
          </div>
        )}

        {isSwitching && (
          <div className="boards-dialog__progress" role="status">
            <div className="boards-dialog__progress-row">
              <span className="boards-dialog__progress-spinner" aria-hidden />
              <span>
                {loadProgress?.message ||
                  (loadingBoardId === currentBoardId
                    ? "正在保存当前画板…"
                    : "正在加载画板…")}
              </span>
            </div>
            {typeof loadProgress?.loadedFiles === "number" &&
              typeof loadProgress?.totalFiles === "number" &&
              loadProgress.totalFiles > 0 && (
                <span className="boards-dialog__progress-bar-wrap">
                  <span
                    className="boards-dialog__progress-bar"
                    style={{
                      width: `${Math.min(
                        100,
                        (100 * loadProgress.loadedFiles) /
                          loadProgress.totalFiles,
                      )}%`,
                    }}
                  />
                </span>
              )}
          </div>
        )}

        {!listLoading && boards.length === 0 && !error && (
          <div className="boards-dialog__empty">暂无画板</div>
        )}

        <ul className="boards-dialog__list">
          {boards.map((board) => {
            const isCurrent = board.id === currentBoardId;
            const isEditing = editingId === board.id;
            const isLoadingThis = loadingBoardId === board.id;
            return (
              <li
                key={board.id}
                className={`boards-dialog__item${
                  isCurrent ? " boards-dialog__item--current" : ""
                }${isLoadingThis ? " boards-dialog__item--loading" : ""}`}
              >
                <div className="boards-dialog__item-main">
                  {isEditing ? (
                    <TextField
                      value={draftTitle}
                      onChange={setDraftTitle}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          void renameBoard(board.id);
                        } else if (event.key === "Escape") {
                          setEditingId(null);
                        }
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="boards-dialog__title-btn"
                      onClick={() => onSwitchBoard(board.id)}
                      disabled={controlsDisabled || isCurrent}
                    >
                      {board.title || "未命名画板"}
                      {isCurrent ? "（当前）" : ""}
                      {isLoadingThis ? " · 加载中" : ""}
                    </button>
                  )}
                  <div className="boards-dialog__meta">
                    {isLoadingThis
                      ? loadProgress?.message || "加载中…"
                      : `更新于 ${formatTime(board.updatedAt)}`}
                  </div>
                </div>
                <div className="boards-dialog__actions">
                  {isEditing ? (
                    <FilledButton
                      label="保存"
                      onClick={() => {
                        void renameBoard(board.id);
                      }}
                      disabled={controlsDisabled}
                    >
                      保存
                    </FilledButton>
                  ) : (
                    <FilledButton
                      label="重命名"
                      variant="outlined"
                      onClick={() => {
                        setEditingId(board.id);
                        setDraftTitle(board.title);
                      }}
                      disabled={controlsDisabled}
                    >
                      重命名
                    </FilledButton>
                  )}
                  <FilledButton
                    label="删除"
                    variant="outlined"
                    color="danger"
                    icon={TrashIcon}
                    onClick={() => {
                      void deleteBoard(board.id);
                    }}
                    disabled={controlsDisabled || boards.length <= 1}
                  >
                    删除
                  </FilledButton>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </Dialog>
  );
};
